// File: frontend/src/api/competitions.ts
import { ApiError } from './auth'; // Assuming ApiError is in auth.ts
import { ApiCompetitionSlim } from './dfs'; // Using the slim version for listing
import { LeagueGameweekMatch } from './schemas';
import { createApiClient, isAxiosError } from './client';

import { API_BASE_URL } from '@/config/apiConfig';
// const API_BASE_URL = 'http://localhost:8000/api/v1';

const competitionsApiClient = createApiClient({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Season structure for the league-creation picker (SPLIT-SEASON.md §4).
// Gameweek numbers are null until the season's schedule (w/ match_week) is
// synced - callers fall back to generic copy.
export interface SeasonStructure {
  fantasy_season_definition_id: number;
  season_identifier: string;
  fall_available: boolean;
  fall_final_gameweek: number | null;
  spring_first_gameweek: number | null;
  // Spring championship gameweek (GW37 rule: never the final day); null when
  // no spring end is configured - fall back to total_gameweeks.
  spring_final_gameweek: number | null;
  total_gameweeks: number | null;
}

export const fetchSeasonStructure = async (competitionId: number): Promise<SeasonStructure> => {
  const response = await competitionsApiClient.get<SeasonStructure>(
    `${API_BASE_URL}/competitions/${competitionId}/season-structure`
  );
  return response.data;
};

export const fetchAllCompetitions = async (
    activeOnly: boolean = true,
    options?: { fantasySeasonReadyOnly?: boolean },
    // token?: string | null // Competitions endpoint is public for now
): Promise<ApiCompetitionSlim[]> => {
    const params = new URLSearchParams({ active_only: String(activeOnly) });
    if (options?.fantasySeasonReadyOnly) {
        params.set('fantasy_season_ready_only', 'true');
    }
    const url = `${API_BASE_URL}/competitions/?${params.toString()}`;
    console.log(`[Competitions API] Fetching competitions: ${url}`);
    try {
        // No auth header needed for this public endpoint currently
        const response = await competitionsApiClient.get<ApiCompetitionSlim[]>(url);
        console.log(`[Competitions API] Received ${response.data.length} competitions.`);
        return response.data;
    } catch (error: unknown) {
        let errorMessage = `Failed to fetch competitions.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error(`[Competitions API] Axios error fetching competitions:`, {
                message: error.message, url: error.config?.url, status, data: error.response?.data,
            });
            const backendError = error.response?.data?.detail;
            if (typeof backendError === 'string') errorMessage = backendError;
        } else {
            console.error(`[Competitions API] Unexpected error fetching competitions:`, error);
        }
        throw new ApiError(errorMessage, status);
    }
};

export const fetchMatchesInWindow = async (
  startUtc: string,
  endUtc: string,
  token: string,
): Promise<LeagueGameweekMatch[]> => {
  if (!token) {
    throw new ApiError('Authentication required to load today\'s matches.', 401);
  }
  const startMs = new Date(startUtc).getTime();
  const endMs = new Date(endUtc).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    throw new ApiError('A valid match window is required.', 400);
  }

  try {
    const response = await competitionsApiClient.get<LeagueGameweekMatch[]>(
      `${API_BASE_URL}/competitions/matches/in-window`,
      {
        params: { start_utc: startUtc, end_utc: endUtc },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  } catch (error: unknown) {
    let errorMessage = 'Failed to load today\'s matches.';
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      const backendError = error.response?.data?.detail;
      if (typeof backendError === 'string') errorMessage = backendError;
    } else if (error instanceof Error && error.message) {
      errorMessage = error.message;
    }
    throw new ApiError(errorMessage, status);
  }
};
